/*
 * Variant media groups
 *
 * Lets a product show a different set of gallery images per variant instead of
 * the single featured image Shopify assigns natively. Grouping is resolved
 * server side by the 'variant-media-groups' snippet, which tags every gallery
 * item with `data-media-group`. Items with an empty group are shared and stay
 * visible for every variant.
 *
 * All media stays in the DOM so crawlers still see the full set. This script
 * only toggles which group is displayed when the shopper changes variant.
 */
(() => {
  const VIEWER_HIDDEN_CLASS = 'product__media-item--group-hidden';
  const THUMBNAIL_HIDDEN_CLASS = 'thumbnail-list__item--group-hidden';

  // Mirrors Liquid's `handleize`, which is what tagged the markup. Comparing on
  // handles means punctuation and casing in the alt text do not have to match
  // the option value exactly, e.g. "Standard - AA Battery" and
  // "Standard – AA Battery" both resolve to "standard-aa-battery".
  const handleize = (value) =>
    (value == null ? '' : String(value))
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const isHidden = (element, activeGroup) => {
    const group = handleize(element.dataset.mediaGroup);
    return group !== '' && group !== activeGroup;
  };

  function findScope(sectionId) {
    if (!sectionId) return null;
    return (
      document.querySelector(`product-info[data-section="${sectionId}"]`) ||
      document.getElementById(`MainProduct-${sectionId}`) ||
      document.getElementById(`MediaGallery-${sectionId}`)?.closest('product-info')
    );
  }

  function updateGallery(gallery, activeGroup) {
    gallery.dataset.activeMediaGroup = activeGroup;

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
    const viewerItems = Array.from(viewer?.querySelectorAll('li[data-media-group]') ?? []);
    if (!viewerItems.length) return;

    // Safety net for incomplete tagging: if no media carries the selected
    // variant's group, show everything rather than an empty gallery.
    const showAll = !viewerItems.some((item) => !isHidden(item, activeGroup));
    let visibleCount = 0;

    viewerItems.forEach((item) => {
      const hidden = !showAll && isHidden(item, activeGroup);
      item.classList.toggle(VIEWER_HIDDEN_CLASS, hidden);
      if (!hidden) visibleCount += 1;
    });

    thumbnails?.querySelectorAll('li[data-media-group]').forEach((item) => {
      item.classList.toggle(THUMBNAIL_HIDDEN_CLASS, !showAll && isHidden(item, activeGroup));
    });

    const counterTotal = gallery.querySelector('.slider-counter--total');
    if (counterTotal) counterTotal.textContent = visibleCount;

    // Slider paging is measured from rendered widths, so recount after filtering.
    viewer?.resetPages?.();
    thumbnails?.resetPages?.();

    ensureActiveMediaVisible(gallery, viewer);
  }

  function ensureActiveMediaVisible(gallery, viewer) {
    if (!viewer) return;

    const active = viewer.querySelector('.product__media-item.is-active');
    if (active && !active.classList.contains(VIEWER_HIDDEN_CLASS)) return;

    const firstVisible = viewer.querySelector(`.product__media-item:not(.${VIEWER_HIDDEN_CLASS})`);
    if (firstVisible?.dataset.mediaId) gallery.setActiveMedia?.(firstVisible.dataset.mediaId, false);
  }

  function updateModal(scope, activeGroup) {
    // The modal is re-rendered from the fetched section markup on most variant
    // changes, but not when the incoming variant has no featured media.
    const items = Array.from(scope.querySelectorAll('.product-media-modal__content [data-media-group]'));
    if (!items.length) return;

    const showAll = !items.some((item) => !isHidden(item, activeGroup));
    items.forEach((item) => {
      item.classList.toggle(VIEWER_HIDDEN_CLASS, !showAll && isHidden(item, activeGroup));
    });
  }

  function onVariantChange({ data }) {
    if (!data?.variant) return;

    const scope = findScope(data.sectionId);
    const gallery = scope?.querySelector('media-gallery[data-variant-media-grouping]');
    if (!gallery) return;

    const optionIndex = Number(gallery.dataset.variantMediaOption) || 0;
    const activeGroup = handleize(data.variant.options?.[optionIndex]);

    updateGallery(gallery, activeGroup);
    updateModal(scope, activeGroup);
  }

  function connect() {
    try {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    } catch (error) {
      // pubsub.js / constants.js have not executed yet.
      return false;
    }

    subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    return true;
  }

  if (!connect()) document.addEventListener('DOMContentLoaded', connect, { once: true });
})();
